import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { LogOut, Plus, Users, Package, FileText, Trash2, X, Upload, Download, Search, Sparkles, Loader2, Pencil } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import TaxonomyManager from "@/components/TaxonomyManager";
import { ImageEditor } from "@/components/ImageEditor";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

const IMG_PLACEHOLDER = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='100' height='100'><rect width='100' height='100' fill='%23F2F2F3'/></svg>";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [buyers, setBuyers] = useState([]);
  const [productsTotal, setProductsTotal] = useState(0);
  const [productsRefreshKey, setProductsRefreshKey] = useState(0);
  const bumpProducts = () => setProductsRefreshKey((k) => k + 1);
  const [categories, setCategories] = useState([]);
  const [selections, setSelections] = useState([]);
  const [buyerDialog, setBuyerDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewSelection, setViewSelection] = useState(null);
  const [productDialog, setProductDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const openNewProduct = () => { setEditingProduct(null); setProductDialog(true); };
  const openEditProduct = (p) => { setEditingProduct(p); setProductDialog(true); };
  const deleteProduct = async (id) => {
    try { await api.delete(`/admin/products/${id}`); toast.success("Product removed"); bumpProducts(); loadAll(); }
    catch { toast.error("Failed to remove product"); }
  };

  const isAdmin = user.role === "admin";
  const perms = user.permissions || {};
  const canProducts = isAdmin || perms.add_edit_products;
  const canStaff = isAdmin || perms.add_new_staff;
  const canManageBuyer = isAdmin || perms.add_buyers || perms.change_buyer_password;
  const [staff, setStaff] = useState([]);
  const [staffDialog, setStaffDialog] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const openNewStaff = () => { setEditingStaff(null); setStaffDialog(true); };
  const openEditStaff = (m) => { setEditingStaff(m); setStaffDialog(true); };
  const [bulkDialog, setBulkDialog] = useState(false);
  const deleteStaff = async (id) => {
    try { await api.delete(`/admin/staff/${id}`); toast.success("Staff removed"); loadAll(); }
    catch { toast.error("Failed to remove staff"); }
  };

  const loadAll = async () => {
    try {
      const reqs = [
        api.get("/admin/buyers"),
        api.get("/admin/products", { params: { limit: 1 } }),
        api.get("/admin/categories"),
        api.get("/admin/selections"),
      ];
      if (canStaff) reqs.push(api.get("/admin/staff"));
      const [b, p, c, s, st] = await Promise.all(reqs);
      setBuyers(b.data);
      setProductsTotal(p.data.total);
      setCategories(c.data);
      setSelections(s.data);
      if (st) setStaff(st.data);
    } catch {
      toast.error("Failed to load data");
    }
  };

  useEffect(() => { loadAll(); }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const openNew = () => { setEditing(null); setBuyerDialog(true); };
  const openEdit = (b) => { setEditing(b); setBuyerDialog(true); };

  const deleteBuyer = async (id) => {
    try {
      await api.delete(`/admin/buyers/${id}`);
      toast.success("Buyer removed");
      loadAll();
    } catch { toast.error("Failed to remove buyer"); }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-black/5">
        <div className="mx-auto max-w-7xl px-6 h-20 flex items-center justify-between">
          <div>
            <div className="brand-mark text-xl">ADASTRA</div>
            <p className="overline text-muted-foreground mt-0.5">{isAdmin ? "Admin Console" : "Staff Console"}</p>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground hidden sm:inline">{user.email}</span>
            <Button variant="ghost" className="rounded-sm h-11" onClick={handleLogout} data-testid="logout-button">
              <LogOut className="h-4 w-4 sm:mr-2" strokeWidth={1.5} aria-hidden="true" />
              <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10">
        <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-10">
          <Stat icon={Users} label="Buyers" value={buyers.length} testid="stat-buyers" />
          <Stat icon={Package} label="Products" value={productsTotal} testid="stat-products" />
          <Stat icon={FileText} label="Selection Sheets" value={selections.length} testid="stat-selections" />
        </div>

        <Tabs defaultValue="buyers">
          <TabsList className="rounded-sm bg-secondary flex flex-wrap h-auto gap-1">
            <TabsTrigger value="buyers" className="rounded-sm" data-testid="tab-buyers">Buyers</TabsTrigger>
            <TabsTrigger value="selections" className="rounded-sm" data-testid="tab-selections">Selection Sheets</TabsTrigger>
            <TabsTrigger value="products" className="rounded-sm" data-testid="tab-products">Products</TabsTrigger>
            {canStaff && <TabsTrigger value="staff" className="rounded-sm" data-testid="tab-staff">Staff</TabsTrigger>}
            {canProducts && <TabsTrigger value="taxonomy" className="rounded-sm" data-testid="tab-taxonomy">Taxonomy</TabsTrigger>}
          </TabsList>

          {/* Buyers */}
          <TabsContent value="buyers" className="mt-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-3xl tracking-tight">Buyer Accounts</h2>
              {(isAdmin || perms.add_buyers) && (
                <Button className="rounded-sm bg-foreground text-background hover:bg-foreground/90" onClick={openNew} data-testid="add-buyer-button">
                  <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} aria-hidden="true" />
                  New Buyer
                </Button>
              )}
            </div>
            <div className="border border-border rounded-sm overflow-x-auto" data-testid="buyers-table">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-secondary">
                  <tr className="text-left">
                    <Th>Name</Th><Th>Email</Th><Th>Access</Th><Th>Login</Th><Th className="text-right">Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {buyers.length === 0 ? (
                    <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">No buyers yet. Create one to get started.</td></tr>
                  ) : buyers.map((b) => {
                    const count = (b.assigned_product_ids?.length || 0);
                    return (
                      <tr key={b.id} className="border-t border-border" data-testid={`buyer-row-${b.email}`}>
                        <td className="p-4 font-medium">{b.name}</td>
                        <td className="p-4 text-muted-foreground">{b.email}</td>
                        <td className="p-4">
                          <div className="flex flex-wrap gap-1">
                            {(b.assigned_categories || []).map((c) => (
                              <Badge key={c} variant="outline" className="rounded-sm">{c}</Badge>
                            ))}
                            {count > 0 && <Badge variant="secondary" className="rounded-sm">{count} items</Badge>}
                            {count === 0 && (b.assigned_categories || []).length === 0 && (
                              <span className="text-muted-foreground text-xs">Nothing assigned</span>
                            )}
                          </div>
                        </td>
                        <td className="p-4"><Badge variant="outline" className="rounded-sm capitalize">{b.auth_provider}</Badge></td>
                        <td className="p-4">
                          <div className="flex justify-end gap-2">
                            {canManageBuyer || isAdmin || perms.remove_buyer ? (
                              <>
                                {canManageBuyer && <Button size="sm" variant="outline" className="rounded-sm" onClick={() => openEdit(b)} data-testid={`edit-buyer-${b.email}`}>Manage</Button>}
                                {(isAdmin || perms.remove_buyer) && (
                                  <Button size="sm" variant="ghost" className="rounded-sm text-destructive hover:text-destructive" onClick={() => deleteBuyer(b.id)} data-testid={`delete-buyer-${b.email}`}>
                                    <Trash2 className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
                                  </Button>
                                )}
                              </>
                            ) : (
                              <span className="text-xs text-muted-foreground">View only</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Selections */}
          <TabsContent value="selections" className="mt-8">
            <h2 className="font-serif text-3xl tracking-tight mb-6">Submitted Selection Sheets</h2>
            <div className="border border-border rounded-sm overflow-x-auto" data-testid="selections-table">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-secondary">
                  <tr className="text-left"><Th>Reference</Th><Th>Buyer</Th><Th>Items</Th><Th>Date</Th><Th className="text-right">View</Th></tr>
                </thead>
                <tbody>
                  {selections.length === 0 ? (
                    <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">No selection sheets submitted yet.</td></tr>
                  ) : selections.map((s) => (
                    <tr key={s.id} className="border-t border-border" data-testid={`selection-row-${s.ref}`}>
                      <td className="p-4 font-medium">{s.ref}</td>
                      <td className="p-4">{s.buyer_name}<span className="block text-xs text-muted-foreground">{s.buyer_email}</span></td>
                      <td className="p-4">{s.items?.length}</td>
                      <td className="p-4 text-muted-foreground">{new Date(s.created_at).toLocaleString()}</td>
                      <td className="p-4 text-right">
                        <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setViewSelection(s)} data-testid={`view-selection-${s.ref}`}>View</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Products */}
          <TabsContent value="products" className="mt-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-3xl tracking-tight">Catalog Products</h2>
              {canProducts && <div className="flex gap-2">
                <Button variant="outline" className="rounded-sm" onClick={() => setBulkDialog(true)} data-testid="bulk-upload-button">
                  <Upload className="h-4 w-4 mr-2" strokeWidth={1.5} aria-hidden="true" />
                  Bulk Upload (CSV)
                </Button>
                <Button className="rounded-sm bg-foreground text-background hover:bg-foreground/90" onClick={openNewProduct} data-testid="add-product-button">
                  <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} aria-hidden="true" />
                  New Product
                </Button>
              </div>}
            </div>
            <ProductsGrid
              canEdit={canProducts}
              categories={categories}
              onEdit={openEditProduct}
              onDelete={deleteProduct}
              refreshKey={productsRefreshKey}
            />
          </TabsContent>

          {canStaff && (
            <TabsContent value="staff" className="mt-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-serif text-3xl tracking-tight">Staff Accounts</h2>
                <Button className="rounded-sm bg-foreground text-background hover:bg-foreground/90" onClick={openNewStaff} data-testid="add-staff-button">
                  <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} aria-hidden="true" />
                  New Staff
                </Button>
              </div>
              <div className="border border-border rounded-sm overflow-x-auto" data-testid="staff-table">
                <table className="w-full text-sm min-w-[640px]">
                  <thead className="bg-secondary">
                    <tr className="text-left"><Th>Name</Th><Th>Email</Th><Th>Position</Th><Th>Permissions</Th><Th className="text-right">Actions</Th></tr>
                  </thead>
                  <tbody>
                    {staff.length === 0 ? (
                      <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">No staff yet. Add a staff member and choose what they can do.</td></tr>
                    ) : staff.map((m) => (
                      <tr key={m.id} className="border-t border-border" data-testid={`staff-row-${m.email}`}>
                        <td className="p-4 font-medium">{m.name}</td>
                        <td className="p-4 text-muted-foreground">{m.email}</td>
                        <td className="p-4" data-testid={`staff-position-${m.email}`}>{m.position || <span className="text-muted-foreground text-xs">—</span>}</td>
                        <td className="p-4">
                          <div className="flex flex-wrap gap-1" data-testid={`staff-perms-${m.email}`}>
                            {PERMISSIONS.filter((p) => m.permissions?.[p.key]).map((p) => (
                              <Badge key={p.key} variant="outline" className="rounded-sm">{p.short}</Badge>
                            ))}
                            {!PERMISSIONS.some((p) => m.permissions?.[p.key]) && <span className="text-muted-foreground text-xs">View only</span>}
                          </div>
                        </td>
                        <td className="p-4 text-right">
                          {isAdmin ? (
                            <div className="flex justify-end gap-2">
                              <Button size="sm" variant="outline" className="rounded-sm" onClick={() => openEditStaff(m)} data-testid={`edit-staff-${m.email}`}>Edit</Button>
                              <Button size="sm" variant="ghost" className="rounded-sm text-destructive hover:text-destructive" onClick={() => deleteStaff(m.id)} data-testid={`delete-staff-${m.email}`}>
                                <Trash2 className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
                              </Button>
                            </div>
                          ) : <span className="text-xs text-muted-foreground">Admin only</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          )}

          {canProducts && <TabsContent value="taxonomy" className="mt-8">
            <div className="mb-6">
              <h2 className="font-serif text-3xl tracking-tight">Category Management</h2>
              <p className="text-sm text-muted-foreground mt-1">Add, rename or remove Types, Categories and Sub-categories. Changes appear instantly in the buyer catalog navigation.</p>
            </div>
            <TaxonomyManager />
          </TabsContent>}
        </Tabs>
      </main>

      <BuyerDialog
        open={buyerDialog}
        onOpenChange={setBuyerDialog}
        editing={editing}
        categories={categories}
        canEdit={isAdmin || !!perms.add_buyers}
        canPassword={isAdmin || !!perms.change_buyer_password}
        onSaved={() => { setBuyerDialog(false); loadAll(); }}
      />

      <ProductDialog
        open={productDialog}
        onOpenChange={setProductDialog}
        editing={editingProduct}
        categories={categories}
        onSaved={() => { setProductDialog(false); bumpProducts(); loadAll(); }}
      />

      <BulkUploadDialog
        open={bulkDialog}
        onOpenChange={setBulkDialog}
        onSaved={() => { setBulkDialog(false); bumpProducts(); loadAll(); }}
      />

      {canStaff && (
        <StaffDialog
          open={staffDialog}
          onOpenChange={setStaffDialog}
          editing={editingStaff}
          allowed={user.permissions || {}}
          onSaved={() => { setStaffDialog(false); loadAll(); }}
        />
      )}

      <Dialog open={!!viewSelection} onOpenChange={(o) => !o && setViewSelection(null)}>
        <DialogContent className="max-w-2xl rounded-sm" data-testid="view-selection-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl tracking-tight">{viewSelection?.ref}</DialogTitle>
            <DialogDescription>
              {viewSelection?.buyer_name} · {viewSelection?.buyer_email}
            </DialogDescription>
          </DialogHeader>
          {viewSelection?.note && (
            <p className="text-sm bg-secondary p-3 rounded-sm"><span className="overline block mb-1">Note</span>{viewSelection.note}</p>
          )}
          <div className="max-h-[45vh] overflow-y-auto divide-y divide-border">
            {viewSelection?.items?.map((it) => (
              <div key={it.id} className="flex items-center gap-4 py-3">
                <div className="h-14 w-14 shrink-0 bg-[#F7F7F8] rounded-sm overflow-hidden"><img src={it.image_url} alt={it.name} className="h-full w-full object-cover" /></div>
                <div className="flex-1 min-w-0">
                  <p className="font-serif text-lg leading-tight truncate">{it.name}</p>
                  <p className="overline text-muted-foreground">{it.sku} · {it.category}</p>
                </div>
                <div className="text-xs text-muted-foreground text-right">
                  <div>Qty {it.quantity || 1}{it.karat ? ` · ${it.karat}` : ""}{it.ring_size ? ` · Size ${it.ring_size}` : ""}</div>
                  <div>Net {it.net_weight}g · Gross {it.gross_weight}g · Stone {it.stone_weight}g</div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BuyerDialog({ open, onOpenChange, editing, categories, canEdit, canPassword, onSaved }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [selCats, setSelCats] = useState([]);
  const [selProducts, setSelProducts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pq, setPq] = useState("");
  const [pResults, setPResults] = useState([]);

  useEffect(() => {
    if (open) {
      setName(editing?.name || "");
      setEmail(editing?.email || "");
      setPassword("");
      setWhatsapp(editing?.whatsapp || "");
      setSelCats(editing?.assigned_categories || []);
      setSelProducts(editing?.assigned_product_ids || []);
    }
  }, [open, editing]);

  const toggleCat = (c) => setSelCats((p) => (p.includes(c) ? p.filter((x) => x !== c) : [...p, c]));
  const toggleProduct = (id) => setSelProducts((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/admin/products", { params: { q: pq.trim() || undefined, limit: 50 } });
        setPResults(data.items);
      } catch { /* ignore */ }
    }, 300);
    return () => clearTimeout(t);
  }, [pq, open]);

  const save = async () => {
    if (!name || !email || (!editing && !password)) {
      toast.error("Name, email and password are required");
      return;
    }
    setSaving(true);
    try {
      const payload = canEdit ? {
        name,
        whatsapp,
        assigned_categories: selCats,
        assigned_product_ids: selProducts,
      } : {};
      if (editing) {
        if (password) payload.password = password;
        await api.put(`/admin/buyers/${editing.id}`, payload);
        toast.success("Buyer updated");
      } else {
        await api.post("/admin/buyers", { ...payload, email: email.trim().toLowerCase(), password });
        toast.success("Buyer created");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save buyer");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl rounded-sm max-h-[90vh] overflow-y-auto" data-testid="buyer-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl tracking-tight">
            {editing ? "Manage Buyer" : "New Buyer Account"}
          </DialogTitle>
          <DialogDescription>Create credentials and assign the catalog this buyer can see.</DialogDescription>
        </DialogHeader>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="overline">Name</Label>
            <Input value={name} disabled={!canEdit} onChange={(e) => setName(e.target.value)} className="h-11 rounded-sm" data-testid="buyer-name-input" />
          </div>
          <div className="space-y-2">
            <Label className="overline">Email</Label>
            <Input type="email" value={email} disabled={!!editing} onChange={(e) => setEmail(e.target.value)} className="h-11 rounded-sm" data-testid="buyer-email-input" />
          </div>
          <div className="space-y-2">
            <Label className="overline">WhatsApp Number</Label>
            <Input value={whatsapp} disabled={!canEdit} onChange={(e) => setWhatsapp(e.target.value)} placeholder="+15551234567" className="h-11 rounded-sm" data-testid="buyer-whatsapp-input" />
          </div>
          <div className="space-y-2">
            <Label className="overline">{editing ? "New Password" : "Password"}</Label>
            <PasswordInput value={password} disabled={!!editing && !canPassword} placeholder={editing ? (canPassword ? "Leave blank to keep" : "No permission to change") : ""} onChange={(e) => setPassword(e.target.value)} className="h-11 rounded-sm" data-testid="buyer-password-input" />
          </div>
        </div>

        {canEdit && <>
        <div className="mt-4">
          <Label className="overline">Assign Entire Categories</Label>
          <div className="flex flex-wrap gap-2 mt-3">
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => toggleCat(c)}
                data-testid={`assign-category-${c.toLowerCase()}`}
                className={`overline px-4 py-2 rounded-sm border transition-colors ${
                  selCats.includes(c) ? "bg-foreground text-background border-foreground" : "border-border hover:bg-secondary"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between">
            <Label className="overline">Or Assign Specific Products</Label>
            {selProducts.length > 0 && <Badge variant="secondary" className="rounded-sm" data-testid="selected-products-count">{selProducts.length} selected</Badge>}
          </div>
          <div className="relative mt-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} aria-hidden="true" />
            <Input value={pq} onChange={(e) => setPq(e.target.value)} placeholder="Search products by name or SKU" className="h-10 rounded-sm pl-9" data-testid="assign-product-search" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3 max-h-64 overflow-y-auto border border-border rounded-sm p-3">
            {pResults.length === 0 ? (
              <p className="text-xs text-muted-foreground col-span-full py-4 text-center">No products found.</p>
            ) : pResults.map((p) => {
              const disabled = selCats.includes(p.category);
              const checked = disabled || selProducts.includes(p.id);
              return (
                <label
                  key={p.id}
                  className={`flex items-center gap-3 p-2 rounded-sm ${disabled ? "opacity-50" : "hover:bg-secondary cursor-pointer"}`}
                  data-testid={`assign-product-${p.sku}`}
                >
                  <Checkbox checked={checked} disabled={disabled} onCheckedChange={() => toggleProduct(p.id)} />
                  <div className="h-9 w-9 shrink-0 bg-[#F7F7F8] rounded-sm overflow-hidden"><img src={p.image_url || IMG_PLACEHOLDER} alt="" className="h-full w-full object-cover" /></div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium truncate">{p.name}</p>
                    <p className="text-[0.65rem] text-muted-foreground">{p.sku}</p>
                  </div>
                </label>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground mt-2">Products in an assigned category are included automatically. Search to find specific items at scale.</p>
        </div>
        </>}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="rounded-sm bg-foreground text-background hover:bg-foreground/90" onClick={save} disabled={saving} data-testid="save-buyer-button">
            {saving ? "Saving…" : editing ? "Save Changes" : "Create Buyer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const PERMISSIONS = [
  { key: "add_buyers", label: "Add & edit buyers", short: "Buyers" },
  { key: "change_buyer_password", label: "Change buyer passwords", short: "Passwords" },
  { key: "remove_buyer", label: "Remove buyers", short: "Remove buyers" },
  { key: "add_new_staff", label: "Add new staff", short: "Add staff" },
  { key: "add_edit_products", label: "Add & edit products", short: "Products" },
];

function StaffDialog({ open, onOpenChange, editing, allowed, onSaved }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [position, setPosition] = useState("");
  const [perms, setPerms] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setName(editing?.name || ""); setEmail(editing?.email || ""); setPassword("");
      setPosition(editing?.position || ""); setPerms(editing?.permissions || {});
    }
  }, [open, editing]);
  const options = PERMISSIONS.filter((p) => allowed[p.key]);
  const save = async () => {
    if (!name || !email || (!editing && !password)) { toast.error("Name, email and password are required"); return; }
    setSaving(true);
    try {
      const payload = { name, email: email.trim().toLowerCase(), position, permissions: perms };
      if (editing) {
        if (password) payload.password = password;
        await api.put(`/admin/staff/${editing.id}`, payload);
        toast.success("Staff updated");
      } else {
        await api.post("/admin/staff", { ...payload, password });
        toast.success("Staff account created");
      }
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save staff"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-sm max-h-[90vh] overflow-y-auto" data-testid="staff-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl tracking-tight">{editing ? "Edit Staff" : "New Staff Account"}</DialogTitle>
          <DialogDescription>Staff can always view buyers and selection sheets. Choose what else they can do.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label className="overline">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="h-11 rounded-sm" data-testid="staff-name-input" /></div>
          <div className="space-y-2"><Label className="overline">Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 rounded-sm" data-testid="staff-email-input" /></div>
          <div className="space-y-2"><Label className="overline">Position</Label>
            <Input value={position} onChange={(e) => setPosition(e.target.value)} placeholder="e.g. Sales Manager" className="h-11 rounded-sm" data-testid="staff-position-input" /></div>
          <div className="space-y-2"><Label className="overline">{editing ? "New Password" : "Password"}</Label>
            <PasswordInput value={password} placeholder={editing ? "Leave blank to keep" : ""} onChange={(e) => setPassword(e.target.value)} className="h-11 rounded-sm" data-testid="staff-password-input" /></div>
          <div className="space-y-2">
            <Label className="overline">Permissions</Label>
            <div className="border border-border rounded-sm divide-y divide-border">
              {options.map((p) => (
                <label key={p.key} className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm cursor-pointer hover:bg-secondary">
                  {p.label}
                  <Switch checked={!!perms[p.key]} onCheckedChange={(v) => setPerms((s) => ({ ...s, [p.key]: v }))} data-testid={`staff-perm-${p.key}`} />
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="rounded-sm bg-foreground text-background hover:bg-foreground/90" onClick={save} disabled={saving} data-testid="save-staff-button">
            {saving ? "Saving…" : editing ? "Save Changes" : "Create Staff"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkUploadDialog({ open, onOpenChange, onSaved }) {
  const [uploading, setUploading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [preview, setPreview] = useState(null);

  const reset = () => setPreview(null);
  useEffect(() => { if (open) reset(); }, [open]);

  const downloadTemplate = async () => {
    try {
      const resp = await api.get("/admin/products/template", { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = "adastra_products_template.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download template"); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setPreview(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/products/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      toast.success(`Previewing ${data.valid} product(s)`);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not read CSV"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const publish = async () => {
    if (!preview?.import_id) return;
    setPublishing(true);
    try {
      const { data } = await api.post(`/admin/products/import/${preview.import_id}/publish`);
      toast.success(`${data.created} product(s) published`);
      reset();
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Publish failed"); }
    finally { setPublishing(false); }
  };

  const discard = async () => {
    if (preview?.import_id) { try { await api.delete(`/admin/products/import/${preview.import_id}`); } catch {} }
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto" data-testid="bulk-upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl tracking-tight">Bulk Catalog Import</DialogTitle>
          <DialogDescription>Upload a CSV, preview what will be imported, then publish.</DialogDescription>
        </DialogHeader>

        {!preview ? (
          <div className="space-y-4">
            <Button variant="outline" className="rounded-sm w-full" onClick={downloadTemplate} data-testid="download-template-button">
              <Download className="h-4 w-4 mr-2" strokeWidth={1.5} aria-hidden="true" />
              Download CSV Template
            </Button>
            <div className="border border-dashed border-border rounded-sm p-6 text-center">
              <Upload className="h-6 w-6 mx-auto text-muted-foreground mb-3" strokeWidth={1.25} aria-hidden="true" />
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer border border-border rounded-sm px-4 py-2 hover:bg-secondary" data-testid="bulk-file-label">
                {uploading ? "Reading…" : "Choose CSV file"}
                <input type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="bulk-file-input" />
              </label>
              <p className="text-xs text-muted-foreground mt-3">Required columns: sku, name, category (image_url &amp; weights optional). Handles large 10k+ row files.</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="import-preview">
            <div className="grid grid-cols-3 gap-3">
              <div className="border border-border rounded-sm p-3 text-center">
                <div className="font-serif text-2xl" data-testid="preview-valid">{preview.valid}</div>
                <p className="overline text-muted-foreground">Ready</p>
              </div>
              <div className="border border-border rounded-sm p-3 text-center">
                <div className="font-serif text-2xl" data-testid="preview-invalid">{preview.invalid}</div>
                <p className="overline text-muted-foreground">Skipped</p>
              </div>
              <div className="border border-border rounded-sm p-3 text-center">
                <div className="font-serif text-2xl">{preview.total_rows}</div>
                <p className="overline text-muted-foreground">Total Rows</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {Object.entries(preview.categories || {}).map(([c, n]) => (
                <Badge key={c} variant="outline" className="rounded-sm">{c}: {n}</Badge>
              ))}
            </div>

            <div className="border border-border rounded-sm overflow-x-auto">
              <table className="w-full text-sm min-w-[560px]">
                <thead className="bg-secondary">
                  <tr className="text-left">
                    <th className="p-2 overline text-muted-foreground font-medium">SKU</th>
                    <th className="p-2 overline text-muted-foreground font-medium">Name</th>
                    <th className="p-2 overline text-muted-foreground font-medium">Category</th>
                    <th className="p-2 overline text-muted-foreground font-medium">Net/Gross/Stone</th>
                  </tr>
                </thead>
                <tbody>
                  {(preview.sample || []).map((r, i) => (
                    <tr key={i} className="border-t border-border" data-testid={`preview-row-${r.sku}`}>
                      <td className="p-2">{r.sku}</td>
                      <td className="p-2">{r.name}</td>
                      <td className="p-2">{r.category}</td>
                      <td className="p-2 text-muted-foreground">{r.net_weight}/{r.gross_weight}/{r.stone_weight}g</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {preview.valid > (preview.sample?.length || 0) && (
              <p className="text-xs text-muted-foreground">Showing first {preview.sample.length} of {preview.valid} products.</p>
            )}
            {preview.errors?.length > 0 && (
              <div className="text-xs bg-secondary rounded-sm p-3" data-testid="preview-errors">
                <p className="font-medium mb-1">{preview.invalid} row(s) skipped:</p>
                <ul className="text-destructive list-disc pl-4">
                  {preview.errors.slice(0, 8).map((er, i) => <li key={i}>{er}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          {preview ? (
            <>
              <Button variant="outline" className="rounded-sm" onClick={discard} data-testid="import-discard-button">Choose Different File</Button>
              <Button className="rounded-sm bg-foreground text-background hover:bg-foreground/90" onClick={publish} disabled={publishing || preview.valid === 0} data-testid="import-publish-button">
                {publishing ? "Publishing…" : `Publish ${preview.valid} Products`}
              </Button>
            </>
          ) : (
            <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProductDialog({ open, onOpenChange, editing, categories, onSaved }) {
  const [form, setForm] = useState({});
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tax, setTax] = useState({ types: [], subcategories: {} });
  const [aiLoading, setAiLoading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorSrc, setEditorSrc] = useState(null);
  const [editorLocal, setEditorLocal] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/taxonomy").then(({ data }) => setTax(data)).catch(() => {});
      setForm(editing ? { ...editing } : {
        sku: "", name: "", type: "", category: "", sub_category: "", image_url: "",
        net_weight: "", gross_weight: "", stone_weight: "", description: "",
      });
    }
  }, [open, editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const categoryOptions = Array.from(new Set([...(tax.categories || []), ...(categories || [])]));

  const aiSuggest = async () => {
    if (!form.type && !form.category && !form.sub_category) {
      toast.error("Select a Type, Category or Sub-category first");
      return;
    }
    setAiLoading(true);
    try {
      const { data } = await api.post("/admin/ai/analyze-image", {
        image_url: form.image_url || undefined,
        type: form.type || "",
        category: form.category || "",
        sub_category: form.sub_category || "",
      });
      if (data.title) {
        set("name", data.title);
        toast.success("Title generated");
      } else {
        toast.error("Could not generate a title");
      }
    } catch {
      toast.error("AI suggestion failed");
    } finally {
      setAiLoading(false);
    }
  };

  const openEditorWithFile = (file) => {
    if (!file || !file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (editorLocal && editorSrc) URL.revokeObjectURL(editorSrc);
    const url = URL.createObjectURL(file);
    setEditorSrc(url); setEditorLocal(true); setEditorOpen(true);
  };

  const onFile = (e) => { openEditorWithFile(e.target.files?.[0]); e.target.value = ""; };

  const onDrop = (e) => { e.preventDefault(); setDragging(false); openEditorWithFile(e.dataTransfer.files?.[0]); };

  const openEditorExisting = () => { setEditorSrc(form.image_url); setEditorLocal(false); setEditorOpen(true); };

  const closeEditor = () => {
    if (editorLocal && editorSrc) URL.revokeObjectURL(editorSrc);
    setEditorSrc(null); setEditorOpen(false);
  };

  const handleEditorSave = async (blob) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", new File([blob], "product.png", { type: "image/png" }));
      const { data } = await api.post("/admin/upload/processed", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("image_url", data.url);
      toast.success("Image saved");
      closeEditor();
    } catch {
      toast.error("Failed to save image");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.sku || !form.name || !form.category || !form.image_url) {
      toast.error("SKU, name, category and image are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        sku: form.sku, name: form.name, type: form.type || "", category: form.category,
        sub_category: form.sub_category || "", image_url: form.image_url,
        net_weight: parseFloat(form.net_weight) || 0,
        gross_weight: parseFloat(form.gross_weight) || 0,
        stone_weight: parseFloat(form.stone_weight) || 0,
        description: form.description || "",
      };
      if (editing) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing ? "Product updated" : "Product created");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save product");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl tracking-tight">
            {editing ? "Edit Product" : "New Product"}
          </DialogTitle>
          <DialogDescription>Upload a photo and enter the piece details.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col sm:flex-row gap-5">
          <div className="shrink-0 w-full sm:w-40">
            <div
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              className={`h-40 w-full sm:w-40 bg-[#F7F7F8] rounded-sm overflow-hidden border border-dashed flex items-center justify-center text-center transition-colors ${dragging ? "border-foreground bg-secondary" : "border-border"}`}
              data-testid="product-dropzone"
            >
              {form.image_url ? (
                <img src={form.image_url} alt="preview" className="h-full w-full object-cover" data-testid="product-image-preview" />
              ) : (
                <div className="px-3">
                  <Upload className="h-6 w-6 mx-auto text-muted-foreground" strokeWidth={1.25} aria-hidden="true" />
                  <p className="text-xs text-muted-foreground mt-2">Drag &amp; drop or use Upload below</p>
                </div>
              )}
            </div>
            <label className="mt-3 flex items-center justify-center gap-2 text-sm cursor-pointer border border-border rounded-sm px-3 py-2 hover:bg-secondary" data-testid="product-upload-label">
              <Upload className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
              {uploading ? "Uploading…" : "Upload"}
              <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="product-image-input" />
            </label>
            {form.image_url && (
              <button
                type="button"
                onClick={openEditorExisting}
                data-testid="edit-image-button"
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-sm border border-border px-3 py-2 text-sm hover:bg-secondary"
              >
                <Pencil className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" /> Edit Image
              </button>
            )}
            <button
              type="button"
              onClick={aiSuggest}
              disabled={aiLoading || (!form.type && !form.category && !form.sub_category)}
              data-testid="ai-suggest-button"
              className="mt-2 flex w-full items-center justify-center gap-2 rounded-sm border border-border px-3 py-2 text-sm hover:bg-secondary disabled:opacity-50"
            >
              {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />}
              {aiLoading ? "Generating…" : "AI Title"}
            </button>
          </div>
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="overline">SKU</Label>
              <Input value={form.sku || ""} onChange={(e) => set("sku", e.target.value)} className="h-11 rounded-sm" data-testid="product-sku-input" />
            </div>
            <div className="space-y-2">
              <Label className="overline">Type</Label>
              <Select value={form.type || ""} onValueChange={(v) => set("type", v)}>
                <SelectTrigger className="h-11 rounded-sm" data-testid="product-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>{(tax.types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="overline">Category</Label>
              <Select value={form.category || ""} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="h-11 rounded-sm" data-testid="product-category-input"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>{categoryOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {(tax.subcategories?.[form.category] || []).length > 0 && (
              <div className="space-y-2">
                <Label className="overline">Sub-category</Label>
                <Select value={form.sub_category || ""} onValueChange={(v) => set("sub_category", v)}>
                  <SelectTrigger className="h-11 rounded-sm" data-testid="product-subcategory-select"><SelectValue placeholder="Select sub-category" /></SelectTrigger>
                  <SelectContent>{(tax.subcategories[form.category] || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2 col-span-2">
              <Label className="overline">Name</Label>
              <Input value={form.name || ""} onChange={(e) => set("name", e.target.value)} className="h-11 rounded-sm" data-testid="product-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="overline">Net Weight (g)</Label>
              <Input type="number" step="0.1" value={form.net_weight} onChange={(e) => set("net_weight", e.target.value)} className="h-11 rounded-sm" data-testid="product-net-input" />
            </div>
            <div className="space-y-2">
              <Label className="overline">Gross Weight (g)</Label>
              <Input type="number" step="0.1" value={form.gross_weight} onChange={(e) => set("gross_weight", e.target.value)} className="h-11 rounded-sm" data-testid="product-gross-input" />
            </div>
            <div className="space-y-2">
              <Label className="overline">Stone Weight (g)</Label>
              <Input type="number" step="0.1" value={form.stone_weight} onChange={(e) => set("stone_weight", e.target.value)} className="h-11 rounded-sm" data-testid="product-stone-input" />
            </div>
          </div>
        </div>

        <div className="space-y-2 mt-2">
          <Label className="overline">Description</Label>
          <Textarea value={form.description || ""} onChange={(e) => set("description", e.target.value)} className="rounded-sm" data-testid="product-description-input" />
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="rounded-sm bg-foreground text-background hover:bg-foreground/90" onClick={save} disabled={saving || uploading} data-testid="save-product-button">
            {saving ? "Saving…" : editing ? "Save Changes" : "Create Product"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <ImageEditor open={editorOpen} imageSrc={editorSrc} onCancel={closeEditor} onSave={handleEditorSave} />
    </>
  );
}

function ProductsGrid({ categories, canEdit, onEdit, onDelete, refreshKey }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  const [cat, setCat] = useState("All");
  const [sub, setSub] = useState("All");
  const [subsMap, setSubsMap] = useState({});
  useEffect(() => { api.get("/taxonomy").then(({ data }) => setSubsMap(data.subcategories || {})).catch(() => {}); }, []);
  const chooseCat = (c) => { setCat(c); setSub("All"); };
  const subOptions = cat === "All" ? [] : (subsMap[cat] || []);
  const filterParams = { q: dq || undefined, category: cat === "All" ? undefined : cat, sub_category: sub === "All" ? undefined : sub };
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const LIMIT = 24;

  useEffect(() => { const t = setTimeout(() => setDq(q.trim()), 300); return () => clearTimeout(t); }, [q]);

  const fetchPage = useCallback(async (nextPage, replace) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/products", { params: { ...filterParams, page: nextPage, limit: LIMIT } });
      setItems((prev) => (replace ? data.items : [...prev, ...data.items]));
      setHasMore(data.has_more); setTotal(data.total); setPage(data.page);
    } catch { toast.error("Failed to load products"); }
    finally { setLoading(false); }
  }, [dq, cat, sub]);

  const refetchCurrent = useCallback(async () => {
    setLoading(true);
    try {
      const all = [];
      let last = null;
      for (let p = 1; p <= Math.max(1, pageRef.current); p++) {
        const { data } = await api.get("/admin/products", { params: { ...filterParams, page: p, limit: LIMIT } });
        all.push(...data.items);
        last = data;
        if (!data.has_more) break;
      }
      setItems(all);
      if (last) { setHasMore(last.has_more); setTotal(last.total); setPage(Math.min(pageRef.current, last.page)); }
    } catch { toast.error("Failed to load products"); }
    finally { setLoading(false); }
  }, [dq, cat, sub]);

  const pageRef = useRef(1);
  useEffect(() => { pageRef.current = page; }, [page]);

  useEffect(() => { fetchPage(1, true); }, [fetchPage]);

  const firstMount = useRef(true);
  useEffect(() => {
    if (firstMount.current) { firstMount.current = false; return; }
    refetchCurrent();
  }, [refreshKey]);

  return (
    <>
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1 sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} aria-hidden="true" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or SKU" className="h-10 rounded-sm pl-9" data-testid="product-search-input" />
        </div>
        <div className="flex flex-wrap gap-2">
          {["All", ...categories].map((c) => (
            <button key={c} onClick={() => chooseCat(c)} data-testid={`admin-cat-${c.toLowerCase()}`}
              className={`overline px-3 py-2 rounded-sm border transition-colors ${cat === c ? "bg-foreground text-background border-foreground" : "border-border hover:bg-secondary"}`}>{c}</button>
          ))}
        </div>
      </div>
      {subOptions.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 -mt-3 mb-6" data-testid="admin-subcategory-filter">
          <span className="overline text-muted-foreground mr-1">Sub-category</span>
          {["All", ...subOptions].map((s) => (
            <button key={s} onClick={() => setSub(s)} data-testid={`admin-sub-${s.toLowerCase().replace(/\s+/g, "-")}`}
              className={`overline px-3 py-1.5 rounded-sm border transition-colors ${sub === s ? "bg-foreground text-background border-foreground" : "border-border hover:bg-secondary"}`}>{s}</button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-testid="admin-product-grid">
        {items.map((p) => (
          <div key={p.id} className="border border-border rounded-sm overflow-hidden group" data-testid={`admin-product-${p.sku}`}>
            <div className="aspect-square bg-[#F7F7F8] relative">
              <img src={p.image_url || IMG_PLACEHOLDER} alt={p.name} className="h-full w-full object-cover" />
              {canEdit && <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <Button size="sm" variant="secondary" className="rounded-sm" onClick={() => onEdit(p)} data-testid={`edit-product-${p.sku}`}>Edit</Button>
                <Button size="sm" variant="secondary" className="rounded-sm text-destructive" onClick={() => onDelete(p.id)} data-testid={`delete-product-${p.sku}`}>
                  <Trash2 className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
                </Button>
              </div>}
            </div>
            <div className="p-3">
              <p className="font-serif text-lg leading-tight">{p.name}</p>
              <p className="overline text-muted-foreground">{p.sku}</p>
              <Badge variant="outline" className="rounded-sm mt-2">{p.category}</Badge>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-8 flex flex-col items-center gap-3">
        {loading && <p className="overline text-muted-foreground">Loading…</p>}
        {!loading && hasMore && <Button variant="outline" className="rounded-sm" onClick={() => fetchPage(page + 1, false)} data-testid="admin-load-more">Load more</Button>}
        {!loading && !hasMore && items.length > 0 && <p className="overline text-muted-foreground" data-testid="admin-product-count">Showing all {total} products</p>}
        {!loading && items.length === 0 && <p className="text-sm text-muted-foreground py-8">No products found.</p>}
      </div>
    </>
  );
}

const Stat = ({ icon: Icon, label, value, testid }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
    className="border border-border rounded-sm p-4 sm:p-6"
    data-testid={testid}
  >
    <Icon className="h-5 w-5 text-muted-foreground mb-3 sm:mb-4" strokeWidth={1.5} aria-hidden="true" />
    <div className="font-serif text-3xl sm:text-4xl tracking-tight">{value}</div>
    <p className="overline text-muted-foreground mt-1">{label}</p>
  </motion.div>
);

const Th = ({ children, className = "" }) => (
  <th className={`p-4 overline text-muted-foreground font-medium ${className}`}>{children}</th>
);
